import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';
import MeterCharts from '../components/MeterCharts';
import { formatDateTime, formatDuration, formatSoc } from '../utils/format';

const History = () => {
  const { addToast } = useToast();

  // Filter state
  const [filters, setFilters] = useState({
    charger_id: '',
    status: '',
    limit: 50
  });
  const [offset, setOffset] = useState(0);

  // Data state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [meterValues, setMeterValues] = useState({});
  const [meterValuesLoading, setMeterValuesLoading] = useState({});

  const api = apiClient;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: filters.limit,
        offset: offset
      };
      if (filters.charger_id.trim()) {
        params.charger_id = filters.charger_id.trim();
      }
      if (filters.status) {
        params.status = filters.status;
      }

      const response = await api.get('/transactions', { params });
      setTransactions(response.data.transactions || response.data || []);
    } catch (err) {
      addToast('Failed to fetch transactions: ' + (err.response?.data?.detail || err.message), 'error');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [api, filters, offset, addToast]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const fetchMeterValues = useCallback(async (transactionId, chargerId) => {
    // Use functional state check to avoid stale closure over meterValues
    let alreadyLoaded = false;
    setMeterValues(prev => {
      if (prev[transactionId]) alreadyLoaded = true;
      return prev;
    });
    if (alreadyLoaded) return;

    setMeterValuesLoading(prev => ({ ...prev, [transactionId]: true }));
    try {
      const params = {
        transaction_id: transactionId,
        limit: 10000
      };
      if (chargerId) {
        params.charger_id = chargerId;
      }

      const response = await api.get('/meter-values', { params });
      setMeterValues(prev => ({ ...prev, [transactionId]: response.data.meter_values || response.data || [] }));
    } catch (err) {
      addToast('Failed to fetch meter values: ' + (err.response?.data?.detail || err.message), 'error');
      setMeterValues(prev => ({ ...prev, [transactionId]: [] }));
    } finally {
      setMeterValuesLoading(prev => ({ ...prev, [transactionId]: false }));
    }
  }, [api, addToast]);

  const handleToggleRow = (transaction) => {
    const txId = transaction.transaction_id;
    if (expandedRow === txId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(txId);
      fetchMeterValues(txId, transaction.charger_id);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    setExpandedRow(null);
    setMeterValues({});
    fetchTransactions();
  };

  const handleClearFilters = () => {
    setFilters({ charger_id: '', status: '', limit: 50 });
    setOffset(0);
    setExpandedRow(null);
    setMeterValues({});
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, offset - filters.limit);
    setOffset(newOffset);
    setExpandedRow(null);
  };

  const handleNextPage = () => {
    setOffset(offset + filters.limit);
    setExpandedRow(null);
  };

  const calculateEnergy = (meterStart, meterStop) => {
    if (meterStart == null) return '-';
    if (meterStop == null) return 'In Progress';
    return ((meterStop - meterStart) / 1000).toFixed(2) + ' kWh';
  };

  const getStatusBadgeStyle = (status) => {
    const base = {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      textTransform: 'capitalize'
    };
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return { ...base, backgroundColor: '#d4edda', color: '#155724' };
      case 'active':
        return { ...base, backgroundColor: '#cce5ff', color: '#004085' };
      case 'failed':
        return { ...base, backgroundColor: '#f8d7da', color: '#721c24' };
      case 'stopped':
        return { ...base, backgroundColor: '#fff3cd', color: '#856404' };
      default:
        return { ...base, backgroundColor: '#e2e3e5', color: '#383d41' };
    }
  };

  const currentPage = Math.floor(offset / filters.limit) + 1;

  // Styles
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '1.5rem'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: 0
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e9ecef'
    },
    filterBar: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem',
      alignItems: 'flex-end'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    label: {
      fontSize: '0.8rem',
      fontWeight: '600',
      color: '#495057'
    },
    input: {
      padding: '0.45rem 0.6rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      minWidth: '160px'
    },
    select: {
      padding: '0.45rem 0.6rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      backgroundColor: '#fff',
      minWidth: '120px'
    },
    btnPrimary: {
      padding: '0.45rem 1rem',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500'
    },
    btnSecondary: {
      padding: '0.45rem 1rem',
      backgroundColor: '#6c757d',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem'
    },
    th: {
      textAlign: 'left',
      padding: '0.65rem 0.75rem',
      borderBottom: '2px solid #dee2e6',
      color: '#495057',
      fontWeight: '600',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.03em'
    },
    td: {
      padding: '0.6rem 0.75rem',
      borderBottom: '1px solid #eee',
      color: '#333'
    },
    trClickable: {
      cursor: 'pointer',
      transition: 'background-color 0.15s ease'
    },
    expandedSection: {
      padding: '1rem 1.5rem',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #dee2e6'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '1rem'
    },
    pageInfo: {
      fontSize: '0.9rem',
      color: '#495057',
      fontWeight: '500'
    },
    loadingText: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6c757d',
      fontSize: '1rem'
    },
    emptyText: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6c757d',
      fontSize: '0.95rem'
    },
    expandIcon: {
      display: 'inline-block',
      width: '18px',
      textAlign: 'center',
      fontWeight: '700',
      color: '#6c757d',
      marginRight: '0.25rem',
      fontSize: '0.8rem'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Charging sessions</h1>
        <p style={{ color: '#555', margin: '0.35rem 0 0' }}>
          Which car, how long, energy, start and end SoC. Expand a row for power, energy, and SoC charts.
        </p>
      </div>

      {/* Filter Bar */}
      <div style={styles.card}>
        <form onSubmit={handleSearch} style={styles.filterBar}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Charger ID</label>
            <input
              type="text"
              placeholder="Filter by charger..."
              value={filters.charger_id}
              onChange={(e) => setFilters({ ...filters, charger_id: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Stopped">Stopped</option>
              <option value="Failed">Failed</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>Limit</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
              style={styles.select}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <button type="submit" style={styles.btnPrimary}>
              Search
            </button>
            <button type="button" onClick={handleClearFilters} style={styles.btnSecondary}>
              Clear Filters
            </button>
          </div>
        </form>
      </div>

      {/* Transactions Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingText}>Loading transactions...</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}></th>
                    <th style={styles.th}>Transaction ID</th>
                    <th style={styles.th}>Charger</th>
                    <th style={styles.th}>Connector</th>
                    <th style={styles.th}>Vehicle</th>
                    <th style={styles.th}>Start</th>
                    <th style={styles.th}>Duration</th>
                    <th style={styles.th}>Energy</th>
                    <th style={styles.th}>SoC</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan={10} style={styles.emptyText}>No transactions found.</td>
                    </tr>
                  )}
                  {transactions.map((tx) => (
                    <React.Fragment key={tx.transaction_id}>
                      <tr
                        onClick={() => handleToggleRow(tx)}
                        style={{
                          ...styles.trClickable,
                          backgroundColor: expandedRow === tx.transaction_id ? '#f0f4ff' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (expandedRow !== tx.transaction_id) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (expandedRow !== tx.transaction_id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <td style={styles.td}>
                          <span style={styles.expandIcon}>
                            {expandedRow === tx.transaction_id ? '\u25BC' : '\u25B6'}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontWeight: '600' }}>{tx.transaction_id}</td>
                        <td style={styles.td}>{tx.charger_id}</td>
                        <td style={styles.td}>{tx.connector_id}</td>
                        <td style={styles.td}>
                          {tx.vehicle_name ? <strong>{tx.vehicle_name}</strong> : null}
                          {tx.vehicle_name ? <div style={{ fontSize: '0.75rem', color: '#666' }}>{tx.id_tag}</div> : (tx.id_tag || '-')}
                        </td>
                        <td style={styles.td}>{formatDateTime(tx.start_timestamp)}</td>
                        <td style={styles.td}>{formatDuration(tx.start_timestamp, tx.stop_timestamp)}</td>
                        <td style={styles.td}>{calculateEnergy(tx.meter_start, tx.meter_stop)}</td>
                        <td style={styles.td}>
                          {formatSoc(tx.soc_start) || '—'}
                          {' → '}
                          {tx.stop_timestamp ? (formatSoc(tx.soc_stop) || '—') : '…'}
                        </td>
                        <td style={styles.td}>
                          <span style={getStatusBadgeStyle(tx.status)}>{tx.status}</span>
                        </td>
                      </tr>

                      {/* Expanded Meter Values */}
                      {expandedRow === tx.transaction_id && (
                        <tr>
                          <td colSpan={10} style={{ padding: 0 }}>
                            <div style={styles.expandedSection}>
                              <h4 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '0.95rem' }}>
                                Session {tx.transaction_id}
                              </h4>
                              {meterValuesLoading[tx.transaction_id] ? (
                                <div style={{ padding: '1rem', color: '#6c757d', textAlign: 'center' }}>
                                  Loading session charts...
                                </div>
                              ) : !meterValues[tx.transaction_id] || meterValues[tx.transaction_id].length === 0 ? (
                                <div style={{ padding: '1rem', color: '#6c757d', textAlign: 'center', fontStyle: 'italic' }}>
                                  No meter samples recorded for this session.
                                </div>
                              ) : (
                                <div style={{ width: '100%', maxWidth: 'calc(100vw - 6rem)' }}>
                                  <MeterCharts rows={meterValues[tx.transaction_id]} />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={styles.pagination}>
              <button
                onClick={handlePrevPage}
                disabled={offset === 0}
                style={{
                  ...styles.btnSecondary,
                  opacity: offset === 0 ? 0.5 : 1,
                  cursor: offset === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>Page {currentPage}</span>
              <button
                onClick={handleNextPage}
                disabled={transactions.length < filters.limit}
                style={{
                  ...styles.btnSecondary,
                  opacity: transactions.length < filters.limit ? 0.5 : 1,
                  cursor: transactions.length < filters.limit ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default History;
